<template lang="pug">
  el-form.entity-autocomplete(ref="form" :model="form" :class="rootClass")
    el-form-item(ref="input" prop="input" :rules="validationRules")
      lei-popover.label-tooltip-box(:text="tip", :placement="tipPlacement")
        label.label(:for="cbId") {{label}}
      el-autocomplete(
        v-model="form.input"
        popper-class="entity-autocomplete__options"
        :fetch-suggestions="fetchOptions"
        :trigger-on-focus="false"
        :clearable="true"
        :placeholder="placeholder"
        :disabled="disabled"
        @select="handleSelect")
        template(slot-scope="{ item }")
          .entity-autocomplete__option
            p.entity-autocomplete__option-name {{item.name}}
            p.entity-autocomplete__option-code {{item.code}}
</template>

<script>
import { mapState, } from 'vuex'
import { createQueryUrl, } from '@/store/helpers.js'
import { baseURL, } from '@/constants/api'
import cdId from '@/mixins/cd-id'
import LeiPopover from '../LeiPopover'
import { EventBus, } from '@/event-bus'

export default {
  name: 'EntityInput',
  components: { LeiPopover, },
  mixins: [ cdId, ],
  props: {
    leiCode: {
      type: String,
      default: '',
    },
    maxRecordsInSuggestion: {
      type: Number,
      default: 5,
    },
    placeholder: {
      type: String,
      default: '',
    },
    rootClass: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    tip: {
      type: [ String, Object, ],
      default: () => ({
        small: '',
        full: '',
      }),
    },
    tipPlacement: {
      type: [ String, ],
      default: 'right',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      form: {
        input: '',
      },
      recordsCache: [],
    }
  },
  computed: {
    input () {
      return this.form.input
    },
    validationRules () {
      return [
        ...this.rules,
        {
          trigger: 'change',
          message: 'The LEI code must be conformed.',
          validator: (rule, value, cb) => {
            if (this.disabled || !value) {
              cb()
              return
            }

            let validEntity = !!this.recordsCache.find((record) => {
              return value === `${record.code}   ${record.name}`
            })
            if (!validEntity) {
              cb(new Error())
            } else {
              cb()
            }
          },
        },
        {
          trigger: 'change',
          message: 'Provided entity is the challenged one. Please, select another entity.',
          validator: (rule, value, cb) => {
            if (this.disabled) {
              cb()
              return
            }

            if (value.startsWith(this.record.lei)) {
              cb(new Error())
            } else {
              cb()
            }
          },
        },
      ]
    },

    ...mapState('record', [ 'record', ]),
  },
  watch: {
    leiCode (newValue) {
      this.fetchOptions(newValue, () => {})
    },
    input (newValue) {
      if (!newValue) {
        this.unselect()
      }

      this.validate((valid) => {
        if (!valid) {
          this.unselect()
        }
      })
    },
    rules () {
      this.$refs.input.clearValidate()
      setTimeout(() => {
        this.validate(() => {})
      }, 100)
    },
  },
  beforeMount () {
    EventBus.$on('clearEntityInputs', () => {
      this.form.input = ''
    })

    this.fetchOptions(this.leiCode, () => {})
  },
  beforeDestroy () {
    EventBus.$off('clearEntityInputs')
  },
  methods: {
    handleSelect (record) {
      this.form.input = `${record.code}   ${record.name}`

      this.$refs.form.validate((valid) => {
        if (valid) {
          this.$emit('select', record)
        }
      })
    },

    unselect () {
      this.$emit('select', {})
    },

    fetchOptions (value, cb) {
      if (!value) {
        return
      }

      const leiCodePattern = /^[A-Z0-9]{18}[0-9]{2}$/
      if (leiCodePattern.test(value)) {
        this.fetchRecordsByLeiCode(value, cb)
      } else {
        this.fetchRecordsByLegalName(value, cb)
      }
    },

    async fetchRecordsByLegalName (namePart, cb) {
      let results = await this.fetchRecords({ 'entity.legalName': namePart, })
      cb(results)
    },
    async fetchRecordsByLeiCode (code, cb) {
      let results = await this.fetchRecords({ 'lei': code, })
      if (results.length === 1 && results[0].code === code) {
        // eslint-disable-next-line standard/no-callback-literal
        cb([])
        this.handleSelect(results[0])
      } else {
        cb(results)
      }
    },
    async fetchRecords (filter) {
      let url = createQueryUrl(baseURL, 'lei-records', {
        filter: {
          ...filter,
          'registration.status': 'ISSUED,LAPSED,PENDING_TRANSFER,PENDING_ARCHIVAL',
        },
        page: {
          number: 1,
          size: this.maxRecordsInSuggestion,
        },
      })
      return this.axios.get(url)
        .then((response) => {
          if (response.data.meta.pagination.total > 0) {
            this.recordsCache = response.data.data.map(({ attributes, }) => {
              return {
                code: attributes.lei,
                name: attributes.entity.legalName.name,
              }
            })
            return this.recordsCache
          } else {
            return []
          }
        })
        .catch((err) => {
          console.error(err)
          return []
        })
    },

    validate (cb) {
      this.$refs.form.validate(cb)
    },
  },
}
</script>

<style lang="stylus">
  @import "~@/assets/styles/variables.styl"

  .entity-autocomplete
    display flex
    flex-direction column

    .label-tooltip-box
      margin-bottom 15px

    .el-form-item
      margin 0

      &__content
        width 100%
        line-height 16px

    .el-autocomplete
      width 100%

    .el-input__inner
      border-radius 0

    &__options
      border 1px solid $default-color-light-2
      border-radius 0
      background-color $white-color
      box-shadow $box-shadow-light

      li
        padding 0

        &.highlighted
          background-color $primary-color-light-3

    &__option
      display flex
      flex-wrap nowrap
      justify-content space-between
      align-items center
      padding 10px 12px
      color $default-color-bold
      font-size .75rem
      font-weight 600

      &:hover, &.highlighted
        background-color $primary-color-light-3

    &__option-name
      max-width 65%
      white-space normal

    &__option-code
      font-family 'Lucida Console', monospace
      font-weight 400
      color $default-color-middle-1
      font-size 90%

  @media (max-width: $mobile)
    .entity-autocomplete
      &__option
        flex-wrap wrap
      &__option-name
        max-width 100%
        min-width 100%
      &__option-code
        max-width 100%
        min-width 100%

</style>



// WEBPACK FOOTER //
// src/components/lei-components/inputs/EntityInput.vue